import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom, of, timeout, catchError } from 'rxjs';
import { PermissionService } from '../../features/security/services/permission.service';
import { AuthService } from '../services/auth.service';

/**
 * Global guard that runs on every protected route.
 *
 * Flow:
 *   1. Super admin → allow.
 *   2. User has at least one role/permission → allow.
 *   3. Otherwise → redirect to /no-role (which shows 10s countdown then logs out).
 */
export const roleCheckGuard: CanActivateFn = async (_route, state) => {
  const router = inject(Router);
  const permissionService = inject(PermissionService);
  const authService = inject(AuthService);

  // Skip check on the /no-role route itself to avoid a redirect loop
  if (state.url.startsWith('/no-role')) {
    return true;
  }

  // Must be authenticated — if not, let the permission guard / login flow handle it
  const isAuth = await authService.isAuthenticated();
  if (!isAuth) {
    return router.createUrlTree(['/login']);
  }

  // Wait for permissions to be initialized
  if (permissionService.loading() || !permissionService.initialized()) {
    if (!permissionService.loading() && !permissionService.initialized()) {
      permissionService.loadPermissions();
    }
    try {
      await firstValueFrom(
        toObservable(permissionService.initialized).pipe(
          filter(init => init),
          timeout(8000),
          catchError(() => of(false))
        )
      );
    } catch {
      return router.createUrlTree(['/login']);
    }
  }

  // Super admin always passes
  if (permissionService.isSuperAdmin()) {
    return true;
  }

  // Tenant admin / user with permissions passes
  if (permissionService.userPermissions().size > 0) {
    return true;
  }

  // No roles assigned → redirect to no-role page (with 10s countdown + logout)
  console.warn('[RoleCheckGuard] User has no role. Redirecting to /no-role.');
  return router.createUrlTree(['/no-role']);
};
